import { Injectable } from '@angular/core';
import { ActivatedRouteSnapshot, Router } from '@angular/router';
import { UserContextService } from 'src/services/user-context.service';
import { RoutePermission } from 'src/app/core/models/route-permission-key';

/**
 * Route Lifecycle Service
 *
 * Handles route-specific lifecycle events that were previously handled by UI-Router onEnter callbacks.
 * This service provides equivalent functionality for Angular Router routes.
 *
 * Key Features:
 * - Replaces individual route onEnter callbacks from UI-Router
 * - Provides state preservation functionality (lastState, lastStateParams)
 * - Handles network route permission-based redirection
 * - Supports route-specific initialization logic
 *
 * Migration Notes:
 * - Replaces individual onEnter functions scattered throughout UI-Router states
 * - Maintains compatibility with existing route parameter handling
 * - Provides hooks for route-specific business logic
 */
@Injectable({
  providedIn: 'root'
})
export class RouteLifecycleService {

  // State preservation (equivalent to UI-Router's lastState tracking)
  private lastRoute: ActivatedRouteSnapshot | null = null;
  private lastParams: any = {};

  constructor(
    private router: Router,
    private userContextService: UserContextService
  ) {}

  /**
   * Handle route entry logic (equivalent to UI-Router onEnter)
   * Call this from route guards or component initialization
   */
  handleRouteEnter(route: ActivatedRouteSnapshot): void {
    const routeName = this.getRouteNameFromSnapshot(route);

    // Store previous route information (for routes that need lastState)
    this.preserveRouteHistory(route);

    // Handle specific route entry logic based on route name
    switch (routeName) {
      // NOTE: 'network' case removed - SmartRedirectGuard on empty child route handles /network redirects
      // Keeping this logic here was causing infinite loops when navigating to child routes like /network/entities

      case 'network.entity.carrierRequest':
      case 'network.agencyProfile.carrierRequest':
      case 'network.carrierAgencyProfile.downlineCarrierRequest':
      case 'dashboard.carrierProfile.downlineCarrierRequest':
      case 'dashboard.carrierRequest':
      case 'dashboard/carrierRequest': // Angular Router format
      case 'dashboard/carrierProfile/downlineCarrierRequest': // Angular Router format
        this.handleCarrierRequestEntry(route);
        break;

      default:
        // No specific entry logic needed for this route
        break;
    }
  }

  /**
   * Handle network route entry with permission-based redirection
   * Equivalent to onNetworkTransitionSuccess from app.routes.ts:532-549
   *
   * DEPRECATED: This method is no longer called. The /network redirect logic is now handled by:
   * - SmartRedirectGuard on the empty child route in network.routes.ts (line 146-154)
   *
   * Calling this method from the parent route caused infinite loops when navigating to child routes
   * like /network/entities because it would re-navigate even though we were already going there.
   */
  private handleNetworkRouteEntry(): void {
    // Check permissions in order and redirect to first available route
    if (this.userContextService.hasPermissionToVisitRoute(RoutePermission.networkEntities)) {
      this.router.navigate(['/network/entities']);
    } else if (this.userContextService.hasPermissionToVisitRoute(RoutePermission.networkAgencyList)) {
      this.router.navigate(['/network/agencyList']);
    } else if (this.userContextService.hasPermissionToVisitRoute(RoutePermission.networkCarrierList)) {
      this.router.navigate(['/network/carrierList']);
    } else if (this.userContextService.hasPermissionToVisitRoute(RoutePermission.networkHierarchyList)) {
      this.router.navigate(['/network/hierarchyList']);
    } else {
      // No permissions for any network routes - redirect to error
      console.error('RouteLifecycleService: No permissions for network routes');
      this.router.navigate(['/error']);
    }
  }

  /**
   * Handle carrier request route entry (preserves navigation state)
   * Equivalent to onEnter callbacks in carrier request routes
   */
  private handleCarrierRequestEntry(route: ActivatedRouteSnapshot): void {
    // Preserve the route that led to this carrier request for back navigation
    // This mimics the UI-Router onEnter logic:
    // state.lastState = transition.from();
    // state.lastStateParams = transition.params('from');

    // Navigation context is stored in this service's private properties (lastRoute, lastParams)
    // Components can access it via getNavigationHistory() method
    // Note: Cannot modify route.data directly as it's frozen/immutable in Angular Router

    console.log('RouteLifecycleService: Preserved navigation state for carrier request', {
      currentRoute: this.getRouteNameFromSnapshot(route),
      lastRoute: this.lastRoute ? this.getRouteNameFromSnapshot(this.lastRoute) : null,
      lastParams: this.lastParams
    });
  }

  /**
   * Preserve route history for back navigation
   */
  private preserveRouteHistory(currentRoute: ActivatedRouteSnapshot): void {
    // Store the previous route as the "last route" for the next navigation
    // This maintains the chain of navigation for back buttons
    if (this.lastRoute) {
      // Don't overwrite if we're navigating to the same route
      const currentRouteName = this.getRouteNameFromSnapshot(currentRoute);
      const lastRouteName = this.getRouteNameFromSnapshot(this.lastRoute);

      if (currentRouteName !== lastRouteName) {
        // Update stored history
        this.lastRoute = currentRoute;
        this.lastParams = currentRoute.params || {};
      }
    } else {
      // First navigation - store current route
      this.lastRoute = currentRoute;
      this.lastParams = currentRoute.params || {};
    }
  }

  /**
   * Get route name from activated route snapshot
   */
  private getRouteNameFromSnapshot(route: ActivatedRouteSnapshot): string {
    // Build hierarchical route name from route path
    const segments: string[] = [];
    let current: ActivatedRouteSnapshot | null = route;

    while (current) {
      if (current.routeConfig?.path && current.routeConfig.path !== '') {
        segments.unshift(current.routeConfig.path);
      }
      current = current.parent;
    }

    return segments.join('.');
  }

  /**
   * Get stored navigation history for components
   * Components can use this to implement "back" functionality
   */
  getNavigationHistory(): { lastRoute: ActivatedRouteSnapshot | null, lastParams: any } {
    return {
      lastRoute: this.lastRoute,
      lastParams: this.lastParams
    };
  }

  /**
   * Clear navigation history (useful for certain workflows)
   */
  clearNavigationHistory(): void {
    this.lastRoute = null;
    this.lastParams = {};
  }
}